import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import * as amplitude from "@amplitude/analytics-browser";
// import { CircleExclamation } from "@unified-checkout/design-system/icons/index";
import type {
  IColumnsMap,
  IFilterMap,
  IFiltersMap,
  TFilters,
  TFiltersParams
} from "libs/design-system/src/types/data_table";
import { arrayToCommaSeparated, DataTable } from "@unified-checkout/design-system";
// import { PageStatus } from "libs/design-system/src/components/PageStatus";
import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import { MOBILE_BREAKPOINT } from "@app/dashboard/constants/break-points";
import { SHARED_EVENTS_PARAMS } from "@app/dashboard/constants/tracking_events/shared";
import {
  TRANSACTIONS_EVENTS,
  TRANSACTIONS_EVENTS_PARAMS,
  TRANSACTIONS_EVENTS_VALUES
} from "@app/dashboard/constants/tracking_events/transactions";
import { INNER_ROUTES } from "@app/dashboard/constants/inner-routes";
import { useScreenWidth } from "@app/dashboard/hooks/useScreenWidth";
import type {
  ITransactionsDateRange,
  ITransactionsStatusFilter
} from "@app/dashboard/types/innerLayout/transactions";
import { useTestLiveModeStore } from "@app/dashboard/store/testLiveMode";
import { useLangStore } from "@app/dashboard/store/lang";
import { getTransactionsFilters } from "@app/dashboard/services/inner_layout/transactions/transactions-filters";
import { getTransactions } from "@app/dashboard/services/inner_layout/transactions";
import { getCountryFromDomain } from "@app/dashboard/utils/getCountryFromDomain";
import { getCurrentMode } from "@app/dashboard/utils/getCurrentMode";
import { getCurrentPath } from "@app/dashboard/utils/getCurrentPath";
import { Header } from "./Header";
import { MetricsBox } from "./MetricsBox";
import styles from "./transactions.module.scss";

const TABLE_ID = 'transaction_table';

export const Transactions = () => {
  const { t } = useTranslation();
  const screenWidth = useScreenWidth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { lang } = useLangStore();

  const { isLive } = useTestLiveModeStore();
  const isMobile = screenWidth <= MOBILE_BREAKPOINT;

  const [isError, setIsError] = useState(false);
  const [dateRange, setDateRange] = useState<ITransactionsDateRange>({
    start_date: "",
    end_date: ""
  });
  const [appliedRangeText, setAppliedRangeText] = useState('');
  const [rangeSelected, setRangeSelected] = useState('');
  const [appliedTransactionsStatus, setAppliedTransactionsStatus] = useState<ITransactionsStatusFilter['name']>('all');

  const columnsMap: IColumnsMap = [
    {
      id: 'amount',
      name: t('innerLayout.transactionsPage.table.headers.amount'),
      type: 'amount',
      key: "amount",
      additionalText: { key: "currency", placement: lang === 'en' ? "start" : "end" }
    },
    {
      id: 'status',
      name: t('innerLayout.transactionsPage.table.headers.status'),
      type: 'badge',
      key: "status_text",
      labelComparingValue: "status",
      label: {
        [`success`]: { color: "green" },
        [`pending`]: { color: "yellow" },
        [`failed`]: { color: "red" },
        [`refunded`]: { color: "grey" },
      },
    },
    {
      id: 'payment_method',
      name: t('innerLayout.transactionsPage.table.headers.method'),
      type: 'payment_method',
      key: "payment_method"
    },
    {
      id: 'customer',
      name: t('innerLayout.transactionsPage.table.headers.customer'),
      type: 'text',
      key: "customer_info.full_name",
      hintText: {
        conditionsType: 'location',
        conditions: {
          "customer_info.phone_number": { "customer_info.phone_number": /(.|\s)*\S(.|\s)*/ }
        },
        default: "customer_info.email"
      },
    },
    {
      id: 'transaction_id',
      name: t('innerLayout.transactionsPage.table.headers.trnxId'),
      type: 'text',
      key: "id"
    },
    {
      id: 'payment_type',
      name: t('innerLayout.transactionsPage.table.headers.type'),
      type: 'text',
      key: "trx_type"
    },
    {
      id: 'order_id',
      name: t('innerLayout.transactionsPage.table.headers.orderId'),
      type: 'text',
      key: "order_id"
    },
    {
      id: 'date',
      name: t('innerLayout.transactionsPage.table.headers.dateCreated'),
      type: 'date',
      key: "created_at",
      hintText: "created_at",
    },
    {
      id: 'source',
      name: t('innerLayout.transactionsPage.table.headers.source'),
      type: 'text',
      key: "source"
    }
  ];

  const {
    isLoading: isFiltersLoading,
    isError: isFiltersError,
    data: filtersData
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_TRANSACTIONS_FILTERS, isLive, lang],
    queryFn: () => getTransactionsFilters({ isLive }),
    select: (response) => response?.data?.data,
  });

  const prevFiltersRef = useRef<IFiltersMap>([]);
  const prevAppliedFiltersRef = useRef<TFiltersParams>({});
  const prevRangeSelectedRef = useRef('');
  const filterClickCounterRef = useRef(0);
  const isInitialRenderRef = useRef(true);

  useEffect(() => {
    prevFiltersRef.current = filtersData ?? prevFiltersRef.current;
  }, [filtersData]);

  useEffect(() => {
    amplitude.track(TRANSACTIONS_EVENTS.TRNX_PAGE_VIEWED, {
      [SHARED_EVENTS_PARAMS.MODE]: getCurrentMode(isLive),
      [TRANSACTIONS_EVENTS_PARAMS.DEFAULT_STATUS_FILTER]: TRANSACTIONS_EVENTS_VALUES.FILTERS.ALL,
    });
  }, []);

  useEffect(() => {
    if (!filtersData?.length) return;
    const keysToRemove = filtersData.map((f: any) => f.filter_key).filter((key: string) => searchParams.has(key));
    if (keysToRemove.length) {
      setSearchParams(prev => {
        keysToRemove.forEach((key: string) => prev.delete(key));
        return prev;
      }, { replace: true });
    }
  }, [filtersData]);

  const filtersMap: IFiltersMap = useMemo(() => (filtersData || prevFiltersRef.current || []).map((filter: any) => {

    const {
      filter_type,
      filter_key,
      filter_name,
      filter_values,
      default_selection,
      filter_place_holder,
      filter_validation
    } = filter;

    const filterType: Record<string, IFilterMap['type']> = {
      dropdown: "select",
      multi_selection_dropdown: "multi_selection",
      text_box: "text",
      phone_number: "phone",
    };

    const searchParamValue = searchParams.get(filter_key);

    return ({
      type: filterType[filter_type],
      key: filter_key,
      name: filter_name,
      values: (filter_type === 'text_box' && searchParamValue) ? searchParamValue : filter_values,
      defaultSelection: default_selection,
      placeHolder: filter_type === 'phone_number' ? undefined : filter_place_holder,
      validations: filter_validation
    })
  }), [filtersData, searchParams]);

  const handleTableRowClick = (transaction: any) => {
    if (transaction) {
      amplitude.track(TRANSACTIONS_EVENTS.TRNX_ROW_OPENED, {
        [TRANSACTIONS_EVENTS_PARAMS.TRNX_ID]: transaction.id,
        [TRANSACTIONS_EVENTS_PARAMS.TRNX_STATUS]: transaction.status,
        [TRANSACTIONS_EVENTS_PARAMS.PAYMENT_METHOD]: transaction.payment_method_name,
        [SHARED_EVENTS_PARAMS.FROM_PAGE]: getCurrentPath(),
      });

      navigate(INNER_ROUTES.SINGLE_TRANSACTION.replace(':id', transaction.id));
    }
  };

  const onResponseChangeHandler = useCallback((
    response: any,
    filters?: TFiltersParams,
    filtersTypes?: Record<TFilters, string[]>
  ) => {
    const count = response?.data?.count;
    const prevFilters = prevAppliedFiltersRef.current;
    const prevRange = prevRangeSelectedRef.current;

    const rangeChanged = rangeSelected !== prevRange;

    const commonParams = {
      ...(rangeSelected ? { [TRANSACTIONS_EVENTS_PARAMS.DATE_RANGE]: rangeSelected } : {}),
      ...(dateRange.start_date ? { [SHARED_EVENTS_PARAMS.START_DATE]: dateRange.start_date } : {}),
      ...(dateRange.end_date ? { [SHARED_EVENTS_PARAMS.END_DATE]: dateRange.end_date } : {}),
      ...(typeof count === "number" ? { [TRANSACTIONS_EVENTS_PARAMS.RESULTS_COUNT]: count } : {}),
      [TRANSACTIONS_EVENTS_PARAMS.CLICK_COUNTER]: filterClickCounterRef.current,
    };

    const allFilterKeys = new Set([...Object.keys(filters || {}), ...Object.keys(prevFilters)]);

    allFilterKeys.forEach((filterName) => {
      const value = (filters || {})[filterName];
      const prevValue = prevFilters[filterName];
      const currentValue = Array.isArray(value) ? JSON.stringify(value) : value;
      const previousValue = Array.isArray(prevValue) ? JSON.stringify(prevValue) : prevValue;
      const isSearchFilter = filtersTypes?.text?.includes(filterName) || filtersTypes?.phone?.includes(filterName);
      const isPhoneFilter = filtersTypes?.phone?.includes(filterName);

      const eventName = isSearchFilter ? TRANSACTIONS_EVENTS.SEARCH_SUBMITTED : TRANSACTIONS_EVENTS.FILTER_APPLIED;
      const paramKeyName = isSearchFilter ? TRANSACTIONS_EVENTS_PARAMS.SEARCH_TYPE : SHARED_EVENTS_PARAMS.FILTER_TYPE;
      const paramValueName = isSearchFilter ? TRANSACTIONS_EVENTS_PARAMS.SEARCH_QUERY : SHARED_EVENTS_PARAMS.FILTER_VALUE;

      const trackingValue = value !== undefined ? (
        Array.isArray(value) ?
          arrayToCommaSeparated(value, true)
          :
          isPhoneFilter ?
            `+${value}`
            :
            value
      )
        :
        '';

      if (currentValue !== previousValue) {
        filterClickCounterRef.current += 1;
        amplitude.track(eventName, {
          [paramKeyName]: filterName,
          [paramValueName]: trackingValue,
          ...commonParams,
        });
      }
    });

    if (
      rangeChanged &&
      !isInitialRenderRef.current &&
      Object.keys(filters || {}).length === 0
    ) {
      filterClickCounterRef.current += 1;
      amplitude.track(TRANSACTIONS_EVENTS.FILTER_APPLIED, commonParams);
    }

    prevAppliedFiltersRef.current = filters || {};
    prevRangeSelectedRef.current = rangeSelected;
    isInitialRenderRef.current = false;
  }, [rangeSelected, dateRange]);

  const queryKeys = useMemo(
    () => [QUERY_KEYS.GET_TRANSACTIONS, isLive, dateRange],
    [isLive, dateRange]
  );

  const queryFunctionParams = useMemo(
    () => ({
      ...dateRange,
      is_live: isLive,
      status: appliedTransactionsStatus
    }),
    [dateRange, isLive, appliedTransactionsStatus]
  );

  const resetFiltersParams = useMemo(
    () => ([
      isLive
    ]),
    [isLive]
  );

  return (
    <div className={styles["transactions-wrapper"]}>
      {
        // isError ?
        //   <PageStatus
        //     status="error"
        //     title={t('innerLayout.errors.smthWentWrong')}
        //     description={t('innerLayout.errors.dataRetrievalErr')}
        //     icon={CircleExclamation}
        //   />
        //   :
        <>
          <Header
            setDateRange={setDateRange}
            setAppliedRangeText={setAppliedRangeText}
            setRangeSelected={setRangeSelected}
            appliedTransactionsStatus={appliedTransactionsStatus}
            setAppliedTransactionsStatus={setAppliedTransactionsStatus}
          />

          <MetricsBox
            dateRange={dateRange}
            comparedRangeText={appliedRangeText}
            appliedTransactionsStatus={appliedTransactionsStatus}
          />

          <DataTable
            lang={lang}

            filters={{
              filtersMap: filtersMap,
              isLoading: isFiltersLoading,
              isError: isFiltersError,
              isMobile: isMobile,
              country: getCountryFromDomain(),
            }}

            tableBody={{
              lang: lang,
              tableId: TABLE_ID,
              dataLocation: "data.results",
              columnsMap: columnsMap,
              rowClickHandler: handleTableRowClick
            }}

            pagination={{
              hideLabels: isMobile,
              type: "cursor_based",
              dataLocation: {
                next: "data.next",
                prev: "data.previous"
              },
              isMobile: isMobile,
            }}

            queries={{
              queryKeys: queryKeys,
              queryFunction: getTransactions,
              queryFunctionParams: queryFunctionParams,
              resetFiltersParams: resetFiltersParams,
              onResponseChange: onResponseChangeHandler
            }}

            errorState={{
              onError: (error) => setIsError(error)
            }}

            emptyState={{
              text: t('innerLayout.transactionsPage.table.body.noTransactionsYet'),
              description: t('innerLayout.transactionsPage.table.body.noTransactionsHint')
            }}
          />
        </>
      }
    </div>
  )
}